import React from "react";
import { Trans } from "react-i18next";
import OnboardingLayout from "@components/onboarding/onboarding_layout";
import WorksitePills from "@components/onboarding/worksite_pills";
import { Location, LocationInput } from "@components/ui/location_input";
import { Body, SectionHeader, SectionTitle } from "@sprout/typography";
import { PrimaryButton, TertiaryButton } from "@sprout/buttons";
import TextInputWithTags from "@components/ui/text_input_with_tags";
import {
  desiredJobTitleValidator,
  MAX_DESIRED_JOB_TITLE_LENGTH,
  MAX_DESIRED_JOB_TITLES,
} from "@utils/desired_job_titles";
import Divider from "@sprout/divider";
import { Checkbox } from "@sprout/checkbox";
import CheckInCircleIcon from "@sprout/icons/check_in_circle";
import useTranslation from "@hooks/use_translation";
import { useSafePostHog } from "@/hooks/use_posthog";
import { JobAlert, useOnboardingPreferencesForm } from "@hooks/use_onboarding_preferences_form";

import "./submit.scss";

type OnboardingJob = {
  title: string;
  companyName: string;
};

type Props = {
  job?: OnboardingJob | null;
  jobAlert?: JobAlert | null;
  desiredJobTitles: string[];
  location?: Location | null;
  paths: {
    workPreferences: string;
    subscriptions: string;
    dashboard: string;
  };
};

const OnboardingSubmit = ({ job, jobAlert, desiredJobTitles, location, paths }: Props) => {
  const { t } = useTranslation("onboarding", { keyPrefix: "submit" });
  const { t: tUser } = useTranslation("user");
  const posthog = useSafePostHog();
  const form = useOnboardingPreferencesForm({ paths, desiredJobTitles, location, jobAlert });

  const onJobAlertChange = () => {
    form.setCreateJobAlert(!form.createJobAlert);

    posthog?.capture("onboarding_create_job_alert", {
      create_alert: !form.createJobAlert ? "on" : "off",
    });
  };

  return (
    <div className="submit-onboarding">
      <div className="submit-onboarding__intro">
        <div className="submit-onboarding__heading">
          <span className="submit-onboarding__check">
            <CheckInCircleIcon size="lg" ariaHidden />
          </span>
          <SectionHeader as="h1" serif large>
            {t("title")}
          </SectionHeader>
        </div>
        <Body>
          {job ? (
            <Trans t={t} i18nKey="body" values={{ company: job.companyName, title: job.title }}>
              <Body as="span" medium>
                {job.title}
              </Body>
            </Trans>
          ) : (
            t("bodyWithoutJob")
          )}
        </Body>
        {job && jobAlert && (
          <div className="submit-onboarding__job-alert">
            <Checkbox
              id="job-alert"
              isMulti
              options={[
                {
                  value: "1",
                  label: (
                    <span className="submit-onboarding__job-alert-label">
                      {t("jobAlertLabel", { company: job.companyName })}
                      <span className="submit-onboarding__job-alert-helper">
                        {t("jobAlertHelperText")}
                      </span>
                    </span>
                  ),
                },
              ]}
              values={form.createJobAlert ? ["1"] : []}
              onSelect={onJobAlertChange}
            />
          </div>
        )}
      </div>

      <Divider />

      <div className="submit-onboarding__questions-intro">
        <SectionTitle>{t("questionsTitle")}</SectionTitle>
        <Body>{t("questionsBody")}</Body>
      </div>

      <section className="submit-onboarding__card">
        <div className="submit-onboarding__field" role="group" aria-labelledby="worksites-question">
          <SectionTitle>
            <span id="worksites-question">{t("worksitesLabel")}</span>
          </SectionTitle>
          <WorksitePills selectedCategories={form.worksites} onChange={form.setWorksites} />
        </div>

        <div className="submit-onboarding__field" role="group" aria-labelledby="location-question">
          <SectionTitle>
            <span id="location-question">{t("locationLabel")}</span>
          </SectionTitle>
          <LocationInput
            value={form.location}
            onChange={form.setLocation}
            helperText={t("locationHelperText")}
          />
        </div>

        <div className="submit-onboarding__field" role="group" aria-labelledby="job-title-question">
          <SectionTitle>
            <span id="job-title-question">{t("jobTitleLabel")}</span>
          </SectionTitle>
          <TextInputWithTags
            id="interested-job-title"
            values={form.jobTitles}
            onChange={form.setJobTitles}
            max={MAX_DESIRED_JOB_TITLES}
            maxLength={MAX_DESIRED_JOB_TITLE_LENGTH}
            maxMessage={tUser("workPreferences.jobTitle.tooMany", { max: MAX_DESIRED_JOB_TITLES })}
            maxReachedText={tUser("workPreferences.jobTitle.maxReached", {
              max: MAX_DESIRED_JOB_TITLES,
            })}
            addLabel={tUser("workPreferences.jobTitle.add")}
            placeholder={t("jobTitlePlaceholder")}
            helperText={t("jobTitleHelperText")}
            validate={desiredJobTitleValidator(tUser)}
            error={form.jobTitlesError}
          />
        </div>
      </section>

      <div className="submit-onboarding__actions">
        <TertiaryButton onClick={form.skip}>{t("skip")}</TertiaryButton>
        <PrimaryButton size="large" loading={form.loading} onClick={form.submit}>
          {t("getMatched")}
        </PrimaryButton>
      </div>

      {form.error && (
        <p className="helper-text helper-text--error" aria-live="polite">
          {form.error}
        </p>
      )}
    </div>
  );
};

OnboardingSubmit.layout = (page: React.ReactNode) => <OnboardingLayout>{page}</OnboardingLayout>;

export default OnboardingSubmit;
